import { QRCodeCanvas } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DeviceMobile, Desktop, Download, Copy, WhatsappLogo, AppleLogo, AndroidLogo, QrCode } from "@phosphor-icons/react";

const OFFICIAL_URL = "https://acertorapidojvo.app.br";

export default function BaixarApp() {
  const copy = () => {
    navigator.clipboard?.writeText(OFFICIAL_URL);
    toast.success("Link copiado!");
  };

  const whatsappShare = `https://api.whatsapp.com/send?text=${encodeURIComponent(
    `🚛 *ACERTO RÁPIDO JVO* — o app do caminhoneiro brasileiro\n\n` +
    `✅ Acerto de viagem em minutos\n✅ GPS ao vivo\n✅ Relatórios em PDF\n\n` +
    `👉 Instale agora (não precisa baixar da loja):\n${OFFICIAL_URL}`
  )}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <Download size={32} weight="duotone" className="text-blue-600" /> Baixar o App
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Instale no celular ou abra no PC — não precisa Play Store nem App Store</p>
      </div>

      {/* Link oficial */}
      <Card className="border-2 border-blue-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><QrCode size={22} weight="fill" className="text-blue-600" /> Link oficial</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <div className="bg-white p-4 border-2 border-slate-200 rounded-xl shadow-sm">
              <QRCodeCanvas value={OFFICIAL_URL} size={200} level="H" includeMargin={false} data-testid="download-qr" />
            </div>
            <div className="flex-1 w-full space-y-3">
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">URL do App</p>
                <div className="flex gap-2">
                  <Input readOnly value={OFFICIAL_URL} className="bg-slate-50 font-mono text-sm" data-testid="official-url" />
                  <Button variant="outline" onClick={copy} data-testid="btn-copy-url" className="gap-1 shrink-0"><Copy size={16} /> Copiar</Button>
                </div>
              </div>
              <div className="text-sm text-slate-700 space-y-2 pt-2">
                <p>📸 <strong>Aponte a câmera do celular</strong> pro QR Code ao lado — abre direto no app</p>
                <p>💬 <strong>Compartilhe com motoristas e colegas</strong> via WhatsApp:</p>
                <a href={whatsappShare} target="_blank" rel="noreferrer" data-testid="btn-whatsapp-share">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 w-full md:w-auto">
                    <WhatsappLogo size={18} weight="fill" /> Enviar link no WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Como instalar — Android */}
      <Card data-testid="install-android">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><AndroidLogo size={22} weight="fill" className="text-green-600" /> Instalar no Android</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
            <li>Abra o <strong>Google Chrome</strong> no celular</li>
            <li>Acesse: <strong className="text-blue-700">acertorapidojvo.app.br</strong></li>
            <li>Toque nos <strong>3 pontinhos (⋮)</strong> no canto superior direito</li>
            <li>Escolha <strong>"Adicionar à tela inicial"</strong> ou <strong>"Instalar app"</strong></li>
            <li>Pronto! O ícone aparece junto com seus outros apps 🎉</li>
          </ol>
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            💡 <strong>Dica:</strong> Abra sempre pelo ícone instalado (não pelo navegador) — vai mais rápido e funciona offline
          </div>
        </CardContent>
      </Card>

      {/* Como instalar — iPhone */}
      <Card data-testid="install-ios">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><AppleLogo size={22} weight="fill" className="text-slate-700" /> Instalar no iPhone / iPad</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
            <li>Abra o <strong>Safari</strong> (não funciona com outros navegadores no iPhone)</li>
            <li>Acesse: <strong className="text-blue-700">acertorapidojvo.app.br</strong></li>
            <li>Toque no botão de <strong>compartilhar ⬆️</strong> (em baixo no centro)</li>
            <li>Role e escolha <strong>"Adicionar à Tela de Início"</strong></li>
            <li>Confirme em "Adicionar". Pronto! 🎉</li>
          </ol>
        </CardContent>
      </Card>

      {/* Instalar no PC */}
      <Card data-testid="install-pc">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Desktop size={22} weight="fill" className="text-blue-600" /> Instalar no computador</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
            <li>Abra o site <strong className="text-blue-700">acertorapidojvo.app.br</strong> no Chrome ou Edge</li>
            <li>Olha a <strong>barra de endereço (à direita)</strong>: um pequeno ícone de "instalar" aparece 💻</li>
            <li>Clique nele e confirme <strong>"Instalar"</strong></li>
            <li>O app abre numa janela própria, como se fosse um programa normal do Windows/Mac</li>
          </ol>
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
            🚀 <strong>Vantagens de instalar:</strong> abre mais rápido, não precisa procurar aba no navegador, funciona offline, aparece no menu Iniciar/Launchpad
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
